<template>
  <div class="clinic-service-card">
    <div class="clinic-service-card__avatar">
      <UIAvatar
          :fullName="clinic.name"
          :photo="clinic.logo || '/images/no-photo.webp'"
          :id="clinic.id"
          :rating="clinic.rating"
          :reviewsCount="clinic.reviewCount"
          :detailUrl="detailUrl"
          model="clinics"
          preset="clinicLogo"
          :addReviewButton="false"
      />
    </div>

    <div class="clinic-service-card__content">
      <div class="clinic-service-card__details">
        <NuxtLink :to="detailUrl" class="clinic-service-card__title">{{ clinic.name }}</NuxtLink>
        <NuxtLink
            v-if="hasVisibleRating"
            :to="detailUrl ? `${detailUrl}#reviews` : '#reviews'"
            class="clinic-service-card__rating"
        >
          <ReviewRatingWidget :rating="clinic.rating" :reviews="clinic.reviewCount"/>
        </NuxtLink>
        <div v-if="clinic.address" class="clinic-service-card__address" v-html="sanitizeHtml(clinic.address)"/>
        <div v-if="clinic.schedule" class="clinic-service-card__schedule" v-html="sanitizeHtml(clinicSchedulePlain(clinic.schedule))"/>
      </div>

      <Transition name="clinic-service-card-schedule">
        <ClinicServicesSchedule
            v-if="clinicServices.length"
            :clinic="clinic"
            :services="clinicServices"
            :serviceId="serviceId"
            :detailUrl="detailUrl"
            :collapseAfter="5"
            :allUrl="allServicesUrl"
        />
      </Transition>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type {TClinic, TClinicService} from "~/types/clinic";
import { sanitizeHtml } from '~/utils/sanitizeHtml';
import { clinicSchedulePlain } from '~/utils/clinicScheduleLabel';

const props = defineProps<{
  clinic: TClinic;
  serviceId?: number;
  /** Слаг узла каталога: якорь его группы в прайсе на странице клиники. */
  nodeSlug?: string;
}>();

const detailUrl = computed<string>(() => {
  if (!props.clinic.slug) {
    return "";
  }
  return `/clinics/${props.clinic.citySlug}/${props.clinic.slug}/`;
});

const allServicesUrl = computed<string>(() => {
  if (!detailUrl.value) {
    return "";
  }
  return props.nodeSlug ? `${detailUrl.value}#${props.nodeSlug}` : detailUrl.value;
});

const clinicServices = computed<TClinicService[]>(() => {
  if (Array.isArray(props.clinic.services)) {
    return props.clinic.services;
  }
  return [];
});

const hasVisibleRating = computed<boolean>(() => {
  const rating = Number(props.clinic.rating);
  const hasRating = Number.isFinite(rating) && rating > 0;
  const hasReviews = Number(props.clinic.reviewCount) > 0;
  return hasRating || hasReviews;
});
</script>

<style lang="scss">
.clinic-service-card {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  background-color: $color-white;
  border-radius: 32px;
  box-shadow: 0 4px 40px 0 rgb(138 138 176 / 10%);
  padding: 24px;

  &__avatar {
    width: 160px;
    flex: 0 0 160px;
  }

  &__content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__details {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__title {
    color: $color-black;
    font-size: 24px;
    font-weight: 500;
    text-decoration: none;
    line-height: 1.2;
  }

  &__rating {
    display: none;
    width: fit-content;
    border-radius: 6px;
    padding: 4px;
    text-decoration: none;
    background-color: $color-dark-white;
  }

  &__address,
  &__schedule {
    color: $color-black;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.35;
    white-space: pre-line;
  }

  &__schedule {
    color: $color-gray;
  }

  .clinic-services-schedule {
    margin-top: 0;
  }

  &-schedule-enter-active,
  &-schedule-leave-active {
    transition: opacity .28s ease, transform .28s ease;
  }

  &-schedule-enter-from,
  &-schedule-leave-to {
    opacity: 0;
    transform: translateY(6px);
  }

  @include media(sm) {
    display: grid;
    grid-template-columns: 80px minmax(0, 1fr);
    grid-template-areas: "avatar content";
    column-gap: 16px;
    row-gap: 12px;
    padding: 16px;
    border-radius: 32px;

    .clinic-services-schedule__show-all {
      width: 100%;
      text-align: center;
    }

    &__avatar {
      grid-area: avatar;
      width: 80px;
      flex-basis: 80px;
    }

    &__content {
      grid-area: content;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    &__details {
      padding: 0;
      gap: 8px;
      align-items: flex-start;
      min-height: 80px;
    }

    &__title {
      font-size: 20px;
      line-height: 1.2;
    }

    &__address,
    &__schedule {
      font-size: 14px;
      line-height: 1.35;
    }

    &__rating {
      display: inline-flex;
      min-height: 24px;
    }

    .avatar {
      margin-bottom: 0 !important;
    }

    .avatar .avatar__rating,
    .avatar__rating {
      display: none !important;
    }

    .avatar > a {
      display: block;
      width: 100%;
    }

    .avatar__image {
      display: block;
      border-radius: 16px !important;
      width: 100% !important;
      height: auto !important;
      aspect-ratio: 1/1;
      object-fit: cover;
    }

    .rating-widget {
      gap: 8px;
      font-size: 12px;
      font-weight: 500;
      line-height: 1.35;
      color: $color-black;
    }

    .rating-widget-item {
      gap: 2px;
    }

    .rating-widget-item--reviews {
      color: $color-gray;
      font-weight: 400;
    }

    .rating-icon {
      width: 16px;
      height: 16px;
    }

    .clinic-services-schedule__service {
      padding: 12px 16px;
      gap: 8px;
    }

    .clinic-services-schedule__main {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      grid-template-areas:
        "title price"
        "action action";
      column-gap: 8px;
      row-gap: 8px;
      align-items: flex-start;
    }

    .clinic-services-schedule__actions {
      grid-area: price;
      width: auto;
      margin-left: auto;
      justify-content: flex-end;
      align-items: flex-start;
      display: contents;
    }

    .clinic-services-schedule__actions-track {
      display: contents;
    }

    .clinic-services-schedule__price {
      grid-area: price;
      justify-self: end;
      align-self: start;
      width: fit-content;
    }

    .clinic-services-schedule__actions-track > .button,
    .clinic-services-schedule__actions-track > .clinic-services-schedule__close-slots {
      grid-area: action;
      justify-self: start;
      flex-basis: auto;
      width: auto;
      margin-top: 0;
      padding-top: 4px;
      padding-bottom: 4px;
    }

    .clinic-services-schedule__actions-track > .button.outline {
      border: none;
      border-radius: 0;
      background: transparent;
      box-shadow: none;
      min-height: auto;
      padding: 4px 0;
      color: $color-accent-text;
    }

    .clinic-services-schedule__title {
      grid-area: title;
      gap: 4px;
      min-width: 0;
    }

    .clinic-services-schedule__name {
      color: $color-black;
      font-size: 14px;
      font-weight: 500;
      line-height: 1.35;
    }

    .clinic-services-schedule__duration,
    .clinic-services-schedule__doctors-hint,
    .clinic-services-schedule__variants-meta-toggle {
      font-size: 12px;
      line-height: 1.35;
    }

    .clinic-services-schedule {
      width: calc(100% + 96px);
      margin-left: -96px;
    }

    .clinic-services-schedule__service:last-child:not(.clinic-services-schedule__service--expanded) {
      border-bottom: none;
    }
  }
}
</style>
